<script setup>
import DatePicker from 'primevue/datepicker'
import Icon from '@/components/shared/icons/Icon.vue'
import CloseButton from '@/components/shared/buttons/CloseButton.vue'
import {DateTime} from 'luxon'
import {dateFormats} from '@/constants/date-and-time.js'
import {useEnv} from '@/composables/use-env.js'
import {toRefs, computed} from 'vue'

//Emits and props
const emit = defineEmits([
  'input',
  'dirty',
  'close',
  'open',
])
const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  minValue: {
    type: String,
    default: null,
  },
  maxValue: {
    type: String,
    default: null,
  },
  displayFormat: {
    type: String,
    default: dateFormats.TIME,
  },
  inputFormat: {
    type: String,
    default: dateFormats.TIME,
  },
  outputFormat: {
    type: String,
    default: dateFormats.TIME,
  },
  isClearable: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isManualInputAllowed: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  testId: {
    type: String,
    default: undefined,
  },
})

//Refs
const {value, minValue, maxValue, displayFormat, inputFormat, outputFormat, isManualInputAllowed} = toRefs(props)

//Allow manual input for tests only
const {isTest} = useEnv()
const allowManualInput = computed(() => isTest.value || isManualInputAllowed.value)

//Not ideal, but it's the best we can do given the limitations of the PrimeVue DatePicker component
const hourFormat = computed(() => displayFormat.value === dateFormats.TIME ? '12' : '24')

//Helper to convert input values to JS dates
const inputValueToJSDate = value => {
  if (value) {
    return DateTime.fromFormat(value, inputFormat.value).toJSDate()
  }
  return null
}

//Determine model values
const modelValue = computed(() => inputValueToJSDate(value.value))
const minDate = computed(() => inputValueToJSDate(minValue.value))
const maxDate = computed(() => inputValueToJSDate(maxValue.value))

//Handle input
const onInput = date => {

  //Get local and model DateTime instances
  const localDateTime = date ? DateTime.fromJSDate(date) : null
  const modelDateTime = modelValue.value ? DateTime.fromJSDate(modelValue.value) : null

  //Check if any change
  if (Number(localDateTime) === Number(modelDateTime)) {
    return
  }

  //Null/empty value
  if (!localDateTime) {
    const payload = {value: '', hour: null, minute: null, meridiem: null}
    emit('input', payload)
    emit('dirty', payload)
    return
  }

  //Extract components
  const value = localDateTime.toFormat(outputFormat.value)
  const meridiem = localDateTime.toFormat('a')
  const minute = localDateTime.minute
  const hour = localDateTime.hour

  //Emit
  const payload = {value, hour, minute, meridiem}
  emit('input', payload)
  emit('dirty', payload)
}

//Handle manual input (PrimeVue 4.4.1 fix for unit and E2E tests)
const onManualInput = event => {

  //Get input value
  const inputValue = event.target.value
  if (!inputValue) {
    onInput(null)
    return
  }

  //Try to parse and check if valid
  const dateTime = DateTime.fromFormat(inputValue, displayFormat.value)
  if (!dateTime.isValid) {
    return
  }

  //Convert to date and pass to onInput handler
  onInput(dateTime.toJSDate())
}
</script>

<template>
  <DatePicker
    v-bind="$attrs"
    :model-value="modelValue"
    :hour-format="hourFormat"
    :manual-input="allowManualInput"
    :step-minute="15"
    :step-second="60"
    :show-clear="isClearable && !isDisabled && !!value"
    :disabled="isDisabled"
    :invalid="hasError"
    :min-date="minDate"
    :max-date="maxDate"
    :test-id="testId"
    size="large"
    icon-display="input"
    aria-label="Time"
    show-icon
    time-only
    fluid
    @value-change="onInput($event)"
    @input="onManualInput($event)"
    @hide="$emit('close')"
    @show="$emit('open')"
  >
    <template #inputicon="{clickCallback}">
      <Icon
        icon="schedule"
        size="s"
        is-outlined
        is-clickable
        :test-id="testId ? `${testId}_icon` : undefined"
        @click="clickCallback()"
      />
    </template>

    <template #clearicon="{clearCallback}">
      <CloseButton
        aria-label="Clear time"
        class="p-datepicker-clear-icon"
        @click="clearCallback()"
      />
    </template>
  </DatePicker>
</template>
